import type { Commit, Dispatch } from 'vuex'
import api from '@/store/api/donor'
import { sendExceptionToAnalytics } from '@/services/googleAnalytics/errorHandling'
import debug from '@/helpers/debug'
import { readAuthIntent } from '@/helpers/authIntent'
import { LANDING_PAGE } from '@/types'
import {
  SET_LOADING,
  SET_REQUEST_LOADING,
  GLOBAL_ERROR,
  SET_USER_STATEMENTS,
  SET_STATEMENT_YEARS,
  SET_DONOR_DONATIONS,
  SET_AUTHORISATION,
  CLEAR_SWIFTAID_AUTHORISATION,
  SET_SWIFTAID_AUTHORISED,
  SET_AUTHORISATION_STATUS_KNOWN,
  SET_USER,
  SET_POSTED_USER,
  SET_REFERRAL_LINK,
  SET_AUTH,
  RESET_REGISTRATION_STATE,
  SET_TOKEN,
  SET_SWIFTAID_GIVING_PLAN,
  SET_SWIFTAID_GIVING_SUBSCRIPTION,
  SET_SWIFTAID_GIVING_FEATURES,
  SET_DONOR_PLAN_STATUS_KNOWN,
  SET_DASHBOARD_SEEN,
  SET_SCHEDULED_FOR_DELETION,
} from '@/store/mutation-types'

/** Ignores overlapping GET /me/authorisation responses so a stale 404 cannot
 *  overwrite a newer authorised result (common after Agree → dashboard). */
let fetchAuthRequestId = 0

export default {
  // NOTE: authorisation
  async fetchAuth({ commit }: { commit: Commit }) {
    const requestId = ++fetchAuthRequestId
    try {
      const response = await api.fetchAuth()
      // Drop stale responses from earlier in-flight calls.
      if (requestId !== fetchAuthRequestId) {
        return response
      }
      if (
        response.status === 404 ||
        response.status === 409 ||
        typeof response.data === 'string' ||
        !response.data
      ) {
        // No active authorisation, e.g. 404, message string, or empty body
        commit(SET_SWIFTAID_AUTHORISED, false)
        commit(SET_AUTHORISATION, {})
      } else {
        // Response is an object
        // e.g. {"authorised":"2020-02-28","expiry":"2020-04-05","canBeExtended":false}
        commit(SET_SWIFTAID_AUTHORISED, true)
        commit(SET_AUTHORISATION, response.data)
      }
      return response
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#fetchAuth: ${error}`
      )
    } finally {
      if (requestId === fetchAuthRequestId) {
        commit(SET_AUTHORISATION_STATUS_KNOWN, true)
        commit(SET_REQUEST_LOADING, false)
      }
    }
  },
  async postAuth({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
    try {
      commit(SET_REQUEST_LOADING, true)
      const response = await api.postAuth()
      await dispatch('fetchAuth')
      // Signup Agree: stop sending createUser on the next routed GET /me so the
      // mock onboarding donor is not re-opened mid-navigation to the dashboard.
      if (readAuthIntent() === 'signup') {
        commit('SET_AUTH_INTENT', 'login')
        commit('SET_LANDING_PAGE', LANDING_PAGE.DASHBOARD)
      }
      return response
    } catch (error: any) {
      sendExceptionToAnalytics(`/src/store/modules/donor.js#postAuth: ${error}`)
      if (error.response && error.response.data) {
        commit(GLOBAL_ERROR, error.response.data)
      }
      throw error
    } finally {
      commit(SET_REQUEST_LOADING, false)
    }
  },
  async deleteAuth({ commit }: { commit: Commit }, payload: any) {
    try {
      commit(SET_LOADING, true)
      const response = await api.deleteAuth(payload)
      return response
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#deleteAuth: ${error}`
      )
    } finally {
      commit(SET_LOADING, false)
    }
  },
  // NOTE: user
  async fetchUser({
    commit,
    dispatch,
    state,
  }: {
    commit: Commit
    dispatch: Dispatch
    state: any
  }) {
    try {
      commit(SET_REQUEST_LOADING, true)
      debug('[donor] fetchUser', {
        authIntent: readAuthIntent(),
        search: window.location.search,
      })

      let response
      try {
        response = await api.fetchUser()
      } catch (error: any) {
        // No Swiftaid account for this Auth0 identity yet: provision one, then
        // continue as if GET /me had returned it. Applies to both login and
        // signup — POST /me replaces the old GET /me?createUser=true flow.
        if (error.response && error.response.status === 404) {
          debug('[donor] fetchUser 404 → POST /me', {
            authIntent: readAuthIntent(),
            url: error.response?.config?.url,
          })
          response = await api.createUser(state.referralLink)
        } else {
          throw error
        }
      }

      commit(SET_SCHEDULED_FOR_DELETION, false)
      commit(SET_USER, response.data)
      dispatch('accounts/addAccounts', response.data.accounts)
      dispatch(
        'accounts/setPendingEmailVerifications',
        response.data.pendingEmailVerifications ?? []
      )
      // Stay on signup intent until the donor reaches the dashboard (landingPage 100).
      // Flipping to login mid-onboarding breaks mock-api after a reload: GET /me
      // 404 stops firing POST /me and falls back to the active persona.
      // Also skip while still on the login/signup email screens — a race with the
      // mock persona fetch was resetting authIntent and hiding signup-only UI.
      const path =
        typeof window !== 'undefined' ? window.location.pathname : ''
      const onAuthEntrance =
        path.startsWith('/login') ||
        path.startsWith('/signup') ||
        path.includes('/user-not-found')
      if (
        readAuthIntent() === 'signup' &&
        response.data?.landingPage === 100 &&
        !onAuthEntrance
      ) {
        commit('SET_AUTH_INTENT', 'login')
      }
      return response
    } catch (error: any) {
      // POST /me 409: the donor account is scheduled for deletion. Keep the
      // session (they can cancel it) and route to the Whoops screen, which
      // shows deletion copy + Cancel deletion / Log out.
      if (error.response && error.response.status === 409) {
        commit(SET_SCHEDULED_FOR_DELETION, true)
        throw error
      }

      // Could not fetch or provision a donor (e.g. POST /me 404, or an
      // unexpected GET failure): reset auth so the caller lands on user-not-found.
      if (error.response && error.response.status === 404) {
        debug('[donor] fetchUser unresolved 404', {
          authIntent: readAuthIntent(),
          url: error.response?.config?.url,
        })
        commit(GLOBAL_ERROR, null)
        commit(SET_AUTH, false)
        commit(SET_TOKEN, null)
        commit(RESET_REGISTRATION_STATE)
        throw error
      }

      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#fetchUser: ${error}`
      )
    } finally {
      commit(SET_REQUEST_LOADING, false)
    }
  },
  // Cancel a scheduled deletion (POST /me?cancelDeletion=true). On success the
  // donor is returned exactly as GET /me would, so hydrate state as fetchUser does.
  async cancelDonorDeletion({
    commit,
    dispatch,
    state,
  }: {
    commit: Commit
    dispatch: Dispatch
    state: any
  }) {
    try {
      commit(SET_REQUEST_LOADING, true)
      const response = await api.createUser(state.referralLink, true)
      commit(SET_SCHEDULED_FOR_DELETION, false)
      commit(SET_USER, response.data)
      dispatch('accounts/addAccounts', response.data.accounts)
      dispatch(
        'accounts/setPendingEmailVerifications',
        response.data.pendingEmailVerifications ?? []
      )
      return response
    } catch (error: any) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#cancelDonorDeletion: ${error}`
      )
      throw error
    } finally {
      commit(SET_REQUEST_LOADING, false)
    }
  },
  async postDonorInformation({ commit }: { commit: Commit }, payload: any) {
    try {
      commit(SET_POSTED_USER, payload)
      return await api.postDonorInformation(payload)
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#postDonorInformation: ${error}`
      )
    }
  },
  async fetchUserStatements({ commit }: { commit: Commit }) {
    try {
      const response = await api.fetchUserStatements()
      commit(SET_USER_STATEMENTS, response.data)
      return response
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#fetchUserStatements: ${error}`
      )
    }
  },
  async fetchStatementYears({ commit }: { commit: Commit }) {
    try {
      const response = await api.fetchStatementYears()
      commit(SET_STATEMENT_YEARS, response.data)
      return response
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#fetchStatementYears: ${error}`
      )
    }
  },
  async deleteSwiftAidAccount({ commit }: { commit: Commit }) {
    try {
      return await api.deleteSwiftAidAccount()
    } catch (error: any) {
      commit(SET_REQUEST_LOADING, false)
      if (error.response && error.response.status === 404) {
        commit(GLOBAL_ERROR, 'error.delete.notFound')
      }
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#deleteSwiftAidAccount: ${error}`
      )
    }
  },
  // NOTE: donations
  async fetchDonorDonations({ commit }: { commit: Commit }) {
    try {
      const response = await api.fetchDonorDonations()
      commit(SET_DONOR_DONATIONS, response.data)
      return response
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#fetchDonorDonations: ${error}`
      )
    }
  },
  setReferralLink({ commit }: { commit: Commit }, payload: any) {
    commit(SET_REFERRAL_LINK, payload)
  },
  clearSwiftaidAuthorisation({ commit }: { commit: Commit }) {
    commit(CLEAR_SWIFTAID_AUTHORISATION)
  },
  setSwiftaidIsAuthorised({ commit }: { commit: Commit }, payload: any) {
    commit(SET_SWIFTAID_AUTHORISED, payload)
  },
  async postAccountVerificationConfirmationCode(_: any, payload: any) {
    try {
      return await api.postAccountVerificationConfirmationCode(payload)
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#postAccountVerificationConfirmationCode: ${error}`
      )
      throw error
    }
  },
  async fetchDonorPlan({ commit }: { commit: Commit }) {
    try {
      const response = await api.fetchDonorPlan()
      if (response.status === 404 || !response.data?.plan) {
        // No paid plan — treat as free once we know the lookup settled.
        commit(SET_SWIFTAID_GIVING_PLAN, 'free')
        commit(SET_SWIFTAID_GIVING_SUBSCRIPTION, null)
        commit(SET_SWIFTAID_GIVING_FEATURES, null)
        return response
      }
      commit(SET_SWIFTAID_GIVING_PLAN, response.data.plan)
      commit(
        SET_SWIFTAID_GIVING_SUBSCRIPTION,
        response.data.subscription ?? null
      )
      commit(SET_SWIFTAID_GIVING_FEATURES, response.data.features ?? null)
      return response
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#fetchDonorPlan: ${error}`
      )
      // Keep the free default but mark known so UI does not hang on the loader.
      commit(SET_SWIFTAID_GIVING_PLAN, 'free')
      commit(SET_SWIFTAID_GIVING_SUBSCRIPTION, null)
      commit(SET_SWIFTAID_GIVING_FEATURES, null)
    } finally {
      commit(SET_DONOR_PLAN_STATUS_KNOWN, true)
    }
  },
  async createPaymentSession(_: { commit: Commit }) {
    try {
      const response = await api.createPaymentSession()
      return response.data
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#createPaymentSession: ${error}`
      )
      throw error
    }
  },
  async completePaymentSession(
    { dispatch }: { commit: Commit; dispatch: Dispatch },
    sessionId: string
  ) {
    try {
      const response = await api.completePaymentSession(sessionId)
      // Always refresh plan + feature flags (fullGivingRecord, etc.) after checkout.
      await dispatch('fetchDonorPlan')
      return response.data
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#completePaymentSession: ${error}`
      )
      throw error
    }
  },
  async fetchBillingPortalSession(_: { commit: Commit }) {
    try {
      const response = await api.fetchBillingPortalSession()
      return response.data
    } catch (error) {
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#fetchBillingPortalSession: ${error}`
      )
      throw error
    }
  },
  async markDashboardSeen({ commit, state }: { commit: Commit; state: any }) {
    if (state.userDetails?.dashboardSeen === true) {
      return
    }
    try {
      const response = await api.markDashboardSeen()
      const payload = response.data ?? {}
      commit(SET_DASHBOARD_SEEN, {
        dashboardSeen: payload.dashboardSeen !== false,
        dashboardFirstSeenAt: payload.dashboardFirstSeenAt ?? null,
      })
      return response
    } catch (error) {
      console.error('Failed to mark dashboard as seen', error)
      sendExceptionToAnalytics(
        `/src/store/modules/donor.js#markDashboardSeen: ${error}`
      )
    }
  },
}
